import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { Play, Pause, Loader2 } from "lucide-react";

interface AudioWavePlayerProps {
  url: string;
  /** Tema: claro (bolha do interlocutor) ou escuro (bolha própria, fundo azul). */
  variante?: "claro" | "escuro";
  alturaPx?: number;
}

function formatTime(seg: number) {
  if (!isFinite(seg) || seg < 0) seg = 0;
  const m = Math.floor(seg / 60);
  const s = Math.floor(seg % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

/**
 * Player de áudio estilo WhatsApp:
 * - Botão play/pause grande
 * - Waveform clicável (desenhada via wavesurfer.js, decodifica via Web Audio)
 * - Tempo decorrido / total
 * - Velocidade 1x / 1.5x / 2x
 *
 * Como decodifica via Web Audio, funciona em iOS Safari mesmo quando
 * o áudio é audio/webm;codecs=opus (que o <audio> nativo do iOS rejeita).
 */
export function AudioWavePlayer({ url, variante = "claro", alturaPx = 36 }: AudioWavePlayerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WaveSurfer | null>(null);
  const [pronto, setPronto] = useState(false);
  const [tocando, setTocando] = useState(false);
  const [tempo, setTempo] = useState(0);
  const [duracao, setDuracao] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [velocidade, setVelocidade] = useState<1 | 1.5 | 2>(1);

  const corOnda = variante === "escuro" ? "#bfdbfe" : "#94a3b8";
  const corProgresso = variante === "escuro" ? "#ffffff" : "#2563eb";

  useEffect(() => {
    if (!containerRef.current) return;
    setPronto(false);
    setErro(null);
    setTempo(0);
    setDuracao(0);

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: corOnda,
      progressColor: corProgresso,
      cursorColor: "transparent",
      barWidth: 2,
      barGap: 2,
      barRadius: 2,
      height: alturaPx,
      normalize: true,
      url,
    });
    wsRef.current = ws;

    const offReady = ws.on("ready", () => { setPronto(true); setDuracao(ws.getDuration()); });
    const offTime = ws.on("audioprocess", (t) => setTempo(t));
    const offSeek = ws.on("seeking", (t) => setTempo(t));
    const offPlay = ws.on("play", () => setTocando(true));
    const offPause = ws.on("pause", () => setTocando(false));
    const offFinish = ws.on("finish", () => setTocando(false));
    const offError = ws.on("error", (e) => setErro((e as any)?.message || "Falha ao carregar áudio"));

    return () => {
      offReady(); offTime(); offSeek(); offPlay(); offPause(); offFinish(); offError();
      try { ws.destroy(); } catch {}
      wsRef.current = null;
    };
  }, [url, alturaPx, corOnda, corProgresso]);

  function toggle() {
    const ws = wsRef.current;
    if (!ws || !pronto) return;
    if (tocando) ws.pause();
    else ws.play();
  }

  function ciclarVelocidade() {
    const proxima: 1 | 1.5 | 2 = velocidade === 1 ? 1.5 : velocidade === 1.5 ? 2 : 1;
    setVelocidade(proxima);
    wsRef.current?.setPlaybackRate(proxima, true);
  }

  const corBotao =
    variante === "escuro"
      ? "bg-white/20 hover:bg-white/30 text-white"
      : "bg-blue-600 hover:bg-blue-700 text-white";

  const corTexto = variante === "escuro" ? "text-white/85" : "text-slate-500";
  const corVel = variante === "escuro"
    ? "bg-white/15 hover:bg-white/25 text-white/90"
    : "bg-slate-100 hover:bg-slate-200 text-slate-600";

  if (erro) {
    return (
      <div className="text-xs text-red-500 italic">
        Não foi possível tocar este áudio neste dispositivo.{" "}
        <a href={url} className="underline" target="_blank" rel="noreferrer">Baixar</a>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 min-w-[200px]">
      <button
        onClick={toggle}
        disabled={!pronto}
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${corBotao}`}
        aria-label={tocando ? "Pausar" : "Tocar"}
      >
        {!pronto ? <Loader2 size={16} className="animate-spin" /> : tocando ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div ref={containerRef} className="w-full" />
        <div className={`flex items-center justify-between text-[10px] mt-0.5 ${corTexto}`}>
          <span className="font-mono">{formatTime(tempo)}</span>
          <span className="font-mono opacity-70">{formatTime(duracao)}</span>
        </div>
      </div>
      <button
        onClick={ciclarVelocidade}
        className={`shrink-0 text-[10px] font-bold px-1.5 py-0.5 rounded ${corVel}`}
        title="Velocidade"
      >
        {velocidade}x
      </button>
    </div>
  );
}
